import React, { useMemo } from 'react';
import SVG from 'react-inlinesvg';
import classNames from 'classnames';

import './icon.scss';

import { type DragonFruitIcon, DragonFruitIcons } from '../../icons/icons';
const LEGACY_ICON_COLORS = ['default', 'dim', 'primary', 'text'];
const DEFAULT_ICON_SIZE_PX = 14;
import type { AutocompletableString } from '@applied/utils/types';

export interface IconProps {
  src: AutocompletableString<DragonFruitIcon>;
  className?: string;
  color?: string;
  /** 'native' height will respect the base height of the svg itself. */
  height?: number | 'native';
  width?: number;
  onLoad?: () => void;
  /**
   * @deprecated
   * Prefer to not use this style to override any CSS properties, since doing so
   * may result in unexpected behavior. This prop is provided for backwards compatibility
   * with the legacy Icon component.
   */
  deprecatedStyle?: React.CSSProperties;
}

function isDragonfruitIcon(src: string): src is DragonFruitIcon {
  return src in DragonFruitIcons;
}

const Icon: React.FC<IconProps> = (props: IconProps) => {
  const {
    height = DEFAULT_ICON_SIZE_PX,
    src,
    color,
    className,
    width,
    onLoad,
    deprecatedStyle,
  } = props;
  const useLegacyColorClass = color && LEGACY_ICON_COLORS.includes(color);
  const styleProps = useMemo((): React.CSSProperties => {
    return {
      height,
      minWidth: height,
      fill: useLegacyColorClass ? undefined : color,
      width,
      ...deprecatedStyle,
    };
  }, [height, width, useLegacyColorClass, color, deprecatedStyle]);
  return (
    <span
      className={classNames('dragon-fruit-icon icon', className, {
        // TODO(youngxguo): deprecate this in favor of using the color prop.
        // Legacy icons have a fill="none" attribute within the svgs themselves
        // that is not overridable via style, but these should be cleaned up
        // so we can trust the color prop.
        [`dragon-fruit-icon--legacy-${color}`]: useLegacyColorClass,
      })}
      style={styleProps}
      data-testid="dragonfruit-icon"
    >
      <SVG
        src={(isDragonfruitIcon(src) ? DragonFruitIcons[src] : src) ?? ''}
        onLoad={onLoad}
        cacheRequests
      />
    </span>
  );
};

export default Icon;
